/**
 * Envio dos anúncios do Mercado Livre: quais anúncios ativos estão em Mercado Envios, quais em "combinar com o vendedor"
 * (sem Flex nem Full possível) e onde o Flex está ligado. Ações em lote: passar para Mercado Envios, ligar e desligar o Flex,
 * pausar e excluir — nos anúncios escolhidos ou em todos do filtro (ex.: todos os recusados).
 */
import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Pause, RefreshCw, Search, Trash2 } from 'lucide-react';
import { api, brl, dateTime, int } from '../api.ts';
import { useMe } from '../components/AuthGate.tsx';
import { ErrorBox, Tag, Thumb } from '../components/ui.tsx';
import { Head, Pager, useSearchBox, useUrlState } from './MeliChannel.tsx';
import { can } from '@berytus/contracts';

type Action = 'me2' | 'flex_on' | 'flex_off' | 'me2_flex' | 'pause' | 'delete';
interface Account {
  id: string; name: string; sellerId: string; flexContracted: boolean | null; active: number; me2: number; notSpecified: number; custom: number; me1: number;
  unread: number; flexOn: number; flexOff: number; flexUnknown: number; full: number; freeShipping: number; errors: number; pending: number; lastActionAt: string | null;
}
interface Row {
  id: string; accountId: string; itemId: string; title: string; sku: string | null; thumbnail: string | null; permalink: string | null; price: number | null;
  stock: number | null; catalog: boolean; mode: string | null; modeLabel: string | null; flex: boolean | null; full: boolean; logisticType: string | null;
  freeShipping: boolean | null; lastAction: Action | null; lastActionAt: string | null; lastError: string | null; pending: boolean;
}
interface Page { accounts: Account[]; total: number; page: number; pageSize: number; rows: Row[] }

const ACTION: Record<Action, { label: string; confirm: string }> = {
  me2: { label: 'Passar para Mercado Envios', confirm: 'passar para Mercado Envios (me2)' },
  me2_flex: { label: 'Mercado Envios + ligar Flex', confirm: 'passar para Mercado Envios e ligar o Flex' },
  flex_on: { label: 'Ligar Flex', confirm: 'ligar o Flex' },
  flex_off: { label: 'Desligar Flex', confirm: 'desligar o Flex' },
  pause: { label: 'Pausar', confirm: 'pausar os anúncios' },
  delete: { label: 'Excluir', confirm: 'finalizar e EXCLUIR os anúncios (não dá para desfazer)' },
};
const LOGISTIC: Record<string, string> = { fulfillment: 'Full', cross_docking: 'Coleta', drop_off: 'Agência', xd_drop_off: 'Places', self_service: 'Flex', default: 'Padrão' };

export function ListingShippingPage() {
  const me = useMe();
  const admin = can(me.tenant.access, 'shipping', 'write') && !me.impersonating;
  const qc = useQueryClient();
  const [params, set] = useUrlState();
  const [search, setSearch] = useSearchBox(params, set);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [notice, setNotice] = useState<string | null>(null);
  const page = Number(params.get('page') ?? 1);
  const filter: Record<string, string> = {};
  for (const k of ['account', 'mode', 'flex', 'full', 'errors', 'q']) if (params.get(k)) filter[k] = params.get(k)!;
  const qs = new URLSearchParams({ ...filter, page: String(page), pageSize: '100' });
  const data = useQuery({
    queryKey: ['listing-shipping', qs.toString()],
    queryFn: () => api<Page>(`/channels/mercadolivre/shipping?${qs}`),
    placeholderData: keepPreviousData,
    refetchInterval: (q) => (q.state.data?.accounts.some((a) => a.pending > 0) ? 5000 : false),
  });
  const d = data.data;
  const accounts = d?.accounts ?? [];
  const account = params.get('account') ? accounts.find((a) => a.id === params.get('account')) ?? null : accounts.length === 1 ? accounts[0]! : null;
  const view = account ? [account] : accounts;
  const tot = (k: keyof Account) => view.reduce((t, a) => t + (Number(a[k]) || 0), 0);
  const notMe2 = tot('notSpecified') + tot('custom') + tot('me1');

  const act = useMutation({
    mutationFn: (b: { action: Action; listingIds?: string[] }) => api<{ queued: number }>('/channels/mercadolivre/shipping/action', {
      method: 'POST', json: { accountId: account!.id, action: b.action, listingIds: b.listingIds, filter: b.listingIds ? undefined : filter },
    }),
    onSuccess: (r) => {
      setPicked(new Set());
      setNotice(r.queued ? `${int(r.queued)} anúncios enviados ao Mercado Livre. A tela atualiza sozinha; os recusados aparecem com o motivo.` : 'Nenhum anúncio precisava dessa alteração.');
      void qc.invalidateQueries({ queryKey: ['listing-shipping'] });
    },
  });
  const pull = useMutation({
    mutationFn: (id: string) => api(`/channels/accounts/${id}/pull`, { method: 'POST' }),
    onSuccess: () => setNotice('Leitura dos anúncios iniciada. Em alguns minutos o envio de cada anúncio aparece aqui.'),
  });
  const run = (action: Action, ids?: string[]) => {
    const n = ids?.length ?? d?.total ?? 0;
    const what = `${ACTION[action].label}: ${ids ? `${int(n)} anúncios escolhidos` : `todos os ${int(n)} anúncios do filtro`} — ${ACTION[action].confirm} no Mercado Livre.`;
    if (action === 'delete') {
      if ((window.prompt(`${what}\n\nPara confirmar, digite EXCLUIR`) ?? '').trim().toUpperCase() !== 'EXCLUIR') return;
    } else if (!window.confirm(`${what} Continuar?`)) return;
    act.mutate({ action, listingIds: ids });
  };

  const kpi = (label: string, value: number, note: string, f: Record<string, string | null>, tone?: 'alert') => {
    const on = Object.entries(f).every(([k, v]) => (params.get(k) ?? null) === v);
    return (
      <button type="button" className={`kpi${on ? ' active' : ''}${tone === 'alert' && value > 0 ? ' alert' : ''}`} style={{ textAlign: 'left', cursor: 'pointer', font: 'inherit' }}
        onClick={() => set({ mode: null, flex: null, full: null, errors: null, ...(on ? {} : f), page: null })}>
        <span className="k-label">{label}</span><span className="k-value">{int(value)}</span><span className="k-note">{note}</span>
      </button>
    );
  };
  const rows = d?.rows ?? [];
  const allPicked = rows.length > 0 && rows.every((r) => picked.has(r.id));

  return (
    <>
      <Head title="Envio dos anúncios">
        <p>Como cada anúncio ativo é enviado. Flex e Full só funcionam em Mercado Envios: anúncios em "combinar com o vendedor" precisam passar para Mercado Envios antes.</p>
      </Head>
      <div className="stack-y">
        {data.error ? <ErrorBox error={data.error} /> : d && (
          <>
            {accounts.length > 1 && (
              <div className="seg" role="group" aria-label="Conta">
                <button type="button" aria-pressed={!params.get('account')} onClick={() => set({ account: null, page: null })}>Todas as contas</button>
                {accounts.map((a) => <button key={a.id} type="button" aria-pressed={params.get('account') === a.id} onClick={() => set({ account: a.id, page: null })}>{a.name}</button>)}
              </div>
            )}
            <div className="kpis">
              {kpi('Anúncios ativos', tot('active'), `${int(tot('full'))} no Full · ${int(tot('freeShipping'))} com frete grátis`, {})}
              {kpi('Mercado Envios', tot('me2'), 'podem ter Flex e Full', { mode: 'me2' })}
              {kpi('Fora do Mercado Envios', notMe2, `${int(tot('notSpecified'))} combinar com o vendedor${tot('custom') ? ` · ${int(tot('custom'))} frete próprio` : ''}`, { mode: 'not_me2' }, 'alert')}
              {kpi('Flex ligado', tot('flexOn'), 'dos que estão em Mercado Envios', { mode: 'me2', flex: 'on' })}
              {kpi('Flex desligado', tot('flexOff') + tot('flexUnknown'), 'em Mercado Envios, sem Flex', { mode: 'me2', flex: 'off' })}
              {kpi('Recusados', tot('errors'), 'a última alteração não passou', { errors: 'true' }, 'alert')}
            </div>
            {view.map((a) => (
              <div key={a.id} className="panel small" style={{ padding: '10px 14px', display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                <b>{a.name}</b>
                <span>Flex na conta: {a.flexContracted == null ? <span className="muted">não foi possível consultar</span> : a.flexContracted ? <Tag tone="ok">contratado</Tag> : <Tag tone="warn">não contratado — ative o Flex no Mercado Livre antes de ligar nos anúncios</Tag>}</span>
                {a.pending > 0 && <span><RefreshCw size={12} /> {int(a.pending)} alterações em andamento…</span>}
                {a.unread > 0 && (
                  <span className="muted">{int(a.unread)} anúncios ainda sem o envio lido.
                    {admin && <button type="button" className="btn small" style={{ marginLeft: 8 }} disabled={pull.isPending} onClick={() => pull.mutate(a.id)}>Ler os anúncios agora</button>}
                  </span>
                )}
              </div>
            ))}
            {notice && <div className="panel small" style={{ padding: '10px 14px' }}>{notice}</div>}
            {act.error && <ErrorBox error={act.error} />}

            <div className="toolbar">
              <label className="search">
                <Search size={16} />
                <span className="sr-only">Buscar</span>
                <input className="input" type="search" placeholder="Título, MLB, SKU ou EAN" value={search} onChange={(e) => setSearch(e.target.value)} />
              </label>
              <select className="select" aria-label="Modo de envio" value={params.get('mode') ?? 'all'} onChange={(e) => set({ mode: e.target.value === 'all' ? null : e.target.value, page: null })}>
                <option value="all">Qualquer modo de envio</option>
                <option value="me2">Mercado Envios</option>
                <option value="not_me2">Fora do Mercado Envios (todos)</option>
                <option value="not_specified">Combinar com o vendedor</option>
                <option value="custom">Frete personalizado</option>
                <option value="me1">Mercado Envios 1 (próprio)</option>
                <option value="unread">Ainda não lido</option>
              </select>
              <select className="select" aria-label="Flex" value={params.get('flex') ?? 'all'} onChange={(e) => set({ flex: e.target.value === 'all' ? null : e.target.value, page: null })}>
                <option value="all">Flex: todos</option>
                <option value="on">Flex ligado</option>
                <option value="off">Flex desligado</option>
                <option value="unknown">Flex: sem informação</option>
              </select>
              <select className="select" aria-label="Full" value={params.get('full') ?? 'all'} onChange={(e) => set({ full: e.target.value === 'all' ? null : e.target.value, page: null })}>
                <option value="all">Full e fora do Full</option>
                <option value="yes">Só Full</option>
                <option value="no">Fora do Full</option>
              </select>
            </div>

            {admin && (
              <div className="toolbar" style={{ marginTop: -4 }}>
                {!account ? <span className="small muted">Escolha a conta acima para alterar o envio dos anúncios.</span> : (
                  <>
                    <span className="small">{picked.size ? <b>{int(picked.size)} escolhidos:</b> : <>Aplicar a <b>todos os {int(d.total)}</b> do filtro:</>}</span>
                    {(['me2', 'me2_flex', 'flex_on', 'flex_off'] as Action[]).map((a) => (
                      <button key={a} type="button" className={`btn small${a === 'flex_off' ? ' ghost' : ''}`} disabled={act.isPending || (!picked.size && !d.total)}
                        onClick={() => run(a, picked.size ? [...picked] : undefined)}>{ACTION[a].label}</button>
                    ))}
                    <span className="small muted" aria-hidden>|</span>
                    <button type="button" className="btn small" disabled={act.isPending || (!picked.size && !d.total)}
                      onClick={() => run('pause', picked.size ? [...picked] : undefined)}><Pause size={13} /> Pausar</button>
                    <button type="button" className="btn small danger" disabled={act.isPending || (!picked.size && !d.total)}
                      onClick={() => run('delete', picked.size ? [...picked] : undefined)}><Trash2 size={13} /> Excluir</button>
                    {picked.size > 0 && <button type="button" className="btn small ghost" onClick={() => setPicked(new Set())}>Limpar escolha</button>}
                  </>
                )}
              </div>
            )}

            <div className="panel flush">
              {!data.isPending && rows.length === 0 ? <div className="empty"><b>Nada por aqui</b>Nenhum anúncio ativo com esse filtro.</div> : (
                <div className="grid-wrap" style={{ opacity: data.isPlaceholderData ? 0.55 : 1 }}>
                  <table className="grid" style={{ width: '100%', minWidth: 1050 }}>
                    <thead>
                      <tr>
                        {admin && account && <th style={{ width: 34 }}><input type="checkbox" className="check" aria-label="Escolher todos da página" checked={allPicked}
                          onChange={(e) => setPicked(e.target.checked ? new Set([...picked, ...rows.map((r) => r.id)]) : new Set([...picked].filter((id) => !rows.some((r) => r.id === id))))} /></th>}
                        <th>Anúncio</th><th style={{ width: 200 }}>Modo de envio</th><th style={{ width: 130 }}>Flex</th><th style={{ width: 110 }}>Logística</th>
                        <th style={{ width: 90 }} className="num">Preço</th><th style={{ width: 70 }} className="num">Estoque</th><th style={{ width: 260 }}>Última alteração</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.id}>
                          {admin && account && <td><input type="checkbox" className="check" aria-label={`Escolher ${r.itemId}`} checked={picked.has(r.id)}
                            onChange={(e) => { const n = new Set(picked); if (e.target.checked) n.add(r.id); else n.delete(r.id); setPicked(n); }} /></td>}
                          <td>
                            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                              <Thumb src={r.thumbnail} alt="" />
                              <div className="cell-2" style={{ minWidth: 0 }}>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title}</span>
                                <span className="sub">
                                  {r.permalink ? <a href={r.permalink} target="_blank" rel="noreferrer" className="code">{r.itemId} <ExternalLink size={11} /></a> : <span className="code">{r.itemId}</span>}
                                  {r.sku ? ` · ${r.sku}` : ''}{r.catalog ? ' · catálogo' : ''}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td>{r.mode == null ? <span className="muted small">ainda não lido</span> : r.mode === 'me2' ? <Tag tone="ok">{r.modeLabel}</Tag> : <Tag tone="warn">{r.modeLabel}</Tag>}
                            {r.freeShipping && <div className="sub small muted">frete grátis</div>}</td>
                          <td>{r.mode !== 'me2' ? <span className="muted small" title="Flex só em Mercado Envios">sem Mercado Envios</span> : r.flex ? <Tag tone="info">ligado</Tag> : r.flex === false ? <Tag tone="off">desligado</Tag> : <span className="muted small">—</span>}</td>
                          <td className="small">{r.logisticType ? LOGISTIC[r.logisticType] ?? r.logisticType : '—'}</td>
                          <td className="num">{brl(r.price)}</td>
                          <td className="num">{r.stock == null ? '—' : int(r.stock)}</td>
                          <td className="small">
                            {r.pending ? <span><RefreshCw size={12} /> {r.lastAction ? ACTION[r.lastAction].label : ''}: em andamento…</span>
                              : r.lastError ? <div className="cell-2"><span style={{ color: 'var(--err)' }}>Recusado: {r.lastAction ? ACTION[r.lastAction].label : ''}</span><span className="sub" title={r.lastError} style={{ whiteSpace: 'normal' }}>{r.lastError}</span></div>
                              : r.lastAction ? <span className="muted">{ACTION[r.lastAction].label} · {r.lastActionAt ? dateTime(r.lastActionAt) : ''}</span> : <span className="muted">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <Pager total={d.total} page={page} pageSize={d.pageSize} onPage={(p) => set({ page: p > 1 ? String(p) : null })} />
            </div>
          </>
        )}
      </div>
    </>
  );
}
